import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { FundProjectionScreenOutlined } from '@ant-design/icons';

import { useStores } from '@/hooks/use-stores';
import { AlertLevels } from '@/constants/enums';
import IconSettings from '@/assets/icon-settings.svg?react';
import logoSmall from '@/assets/logo-small.png';
import SeverityIcon from '@/comp/common/icons/SeverityIcon';

import { Container } from './SideMenu.styles';
import { getTopNavItems, NavItemDetails, parseNavItem } from '../StationPage.utils';
import PopoverMenuUser from './PopoverMenuUser/PopoverMenuUser';
import EPSwitch from '@/comp/EPSwitch';

const SideMenu = observer(() => {
  const { t } = useTranslation();
  const { stationStore, alertStore, userStore } = useStores();
  const currentLocation = useLocation();

  const shouldDisplayAlerts = () => {
    const pathsWithAlerts = ['/tab/map', '/tab/trends'];
    const isTabRoute = pathsWithAlerts.filter(path => currentLocation.pathname.includes(path)).length > 0;
    return stationStore.isSidebarCollapsed && isTabRoute;
  };

  const topNavItems = getTopNavItems(
    t,
    currentLocation.pathname,
    stationStore.isMonitorStationTierForPeriodicReports,
    stationStore.currentStationStage,
  );

  const shutdownAlerts = alertStore.filterOpenAlerts(alertStore.shutdownAlerts).length;
  const warningAlerts = alertStore.filterOpenAlerts(alertStore.warningAlerts).length;
  const serviceAlerts = alertStore.filterOpenAlerts(alertStore.serviceAlerts).length;

  const bottomNavItems: NavItemDetails[] = [
    {
      icon: <IconSettings className="settings-icon" />,
      link: 'settings',
    },
  ];

  return (
    <Container>
      <div className="top">
        <ul className={'top-menu'}>
          <li className="logo-container">
            <img src={logoSmall} className="logo" alt={t(`Eco plant's logo`)} />
          </li>
          {topNavItems.map((item, index) => (
            <li key={index}>{parseNavItem(item)}</li>
          ))}
        </ul>
      </div>

      <div className={'sidebar-bottom'}>
        <ul>
          {shouldDisplayAlerts() && (
            <li>
              <div onClick={stationStore.toggleSidebar} className={'alert-indication-container shutdown-alerts'}>
                <SeverityIcon severity={AlertLevels.Shutdown} width={24} height={24} />
                <span className={'alerts-count-container'}>{shutdownAlerts}</span>
              </div>
            </li>
          )}
          {shouldDisplayAlerts() && (
            <li>
              <div onClick={stationStore.toggleSidebar} className={'alert-indication-container service-alerts'}>
                <SeverityIcon severity={AlertLevels.Service} width={24} height={24} />
                <span className={'alerts-count-container'}>{serviceAlerts}</span>
              </div>
            </li>
          )}
          {shouldDisplayAlerts() && (
            <li className={'end-of-alerts-indications'}>
              <div onClick={stationStore.toggleSidebar} className={'alert-indication-container warning-alerts'}>
                <SeverityIcon severity={AlertLevels.Warning} width={24} height={24} />
                <span className={'alerts-count-container'}>{warningAlerts}</span>
              </div>
            </li>
          )}

          {bottomNavItems.map((item, index) => (
            <li key={index}>{parseNavItem(item)}</li>
          ))}

          <li>
            <PopoverMenuUser />
          </li>
          {userStore.isIrcoEmail && (
            <li className="showcase-switch">
              <EPSwitch
                value={userStore.isShowCaseEnabled}
                onChange={value => {
                  userStore.setShowCaseEnabled(value);
                }}
                checkedChildren={<FundProjectionScreenOutlined />}
                unCheckedChildren={<FundProjectionScreenOutlined />}
              />
            </li>
          )}
        </ul>
      </div>
    </Container>
  );
});

export default SideMenu;
